import React, { useEffect, useState } from 'react';

import {
  MouseMoveEvent,
  OnMouseMoveListener,
  RouteSelectionStyle
} from '@strava/mre';

import {
  END_OBJECT_POSITION,
  MAP_LAYERS,
  START_OBJECT_POSITION
} from '../../constants';
import { getWorldLine2FromLatLngArray } from '../../helpers/getWorldLine2FromLatLngArray';
import { useMRE } from '../../hooks/useMRE';
import { useMRELogError } from '../../hooks/useMRELogError';
import { LatLng, RouteWaypointWithDescription } from '../../types';
import { Popup, PopupProps } from '../Popup/Popup';

interface RouteProps {
  id: string;
  polyline: LatLng[];
  isSelected?: boolean;
  waypoints?: RouteWaypointWithDescription[];
}

/*
 * This component renders a single Route on a map, e.g. RDP
 */
export const Route = ({ id, polyline, isSelected, waypoints }: RouteProps) => {
  const { terrainEngine, t } = useMRE();
  const logError = useMRELogError();

  const [popupProps, setPopupProps] = useState<PopupProps | null>(null);

  useEffect(() => {
    if (!terrainEngine || !polyline) {
      return;
    }

    terrainEngine
      .getContent()
      .getRouteContent()
      .displayRoute(
        id,
        getWorldLine2FromLatLngArray(polyline),
        [],
        waypoints,
        isSelected ? RouteSelectionStyle.CANONICAL : undefined,
        false // keep start / end points
      );

    const handleMouseMove: OnMouseMoveListener = {
      onMouseMove: (evt: MouseMoveEvent) => {
        // We loop over the objects array in order and trust that the engine will return
        // higher priority objects at the start of the array. This is the most performant
        // approach, but of course does rely on the engine's ordering - not sure if that
        // should be trusted or not.

        // NOTE: This solution will NOT currently work well for having MULTIPLE routes rendered with markers.
        // Each of our <Route> components will render their own popups - it will not know that the hover event
        // is associated with THEIR Route. This will need to be handled when we roll out to a map surface which
        // displays multiple routes.
        const willRenderPopup = evt.objects.some((object) => {
          if (object.layerName === MAP_LAYERS.waypoints) {
            // We need to map the waypoint object back to the waypoint in our props, because
            // we need to pull out extra properties (in this case `description`) which the
            // engine isn't aware of.
            const waypoint = waypoints?.find(
              (waypoint) => waypoint.identifier === object.customTapId
            );

            if (waypoint) {
              setPopupProps({
                latLng: [waypoint.point.latitude, waypoint.point.longitude],
                children: (
                  <>
                    <Popup.Title>{waypoint.title}</Popup.Title>
                    {waypoint.description && (
                      <Popup.Paragraph>{waypoint.description}</Popup.Paragraph>
                    )}
                  </>
                )
              });
              return true;
            } else {
              // We should never get here.
              logError(
                'Route attempted to handle waypoint hover for "customTapId" not present in waypoint props list.'
              );
            }
          }

          if (
            object.layerName === MAP_LAYERS.stravaPoints &&
            [START_OBJECT_POSITION, END_OBJECT_POSITION].includes(
              object.position
            )
          ) {
            // We are hovering over a start or end marker.
            setPopupProps({
              latLng: [object.worldPoint.latitude, object.worldPoint.longitude],
              children: (
                <Popup.Title>{t(`mre.popup.${object.position}`)}</Popup.Title>
              )
            });
            return true;
          }

          return false;
        });

        if (!willRenderPopup) {
          setPopupProps(null);
        }
      }
    };

    terrainEngine.addOnMouseMoveListener(handleMouseMove);

    return () => {
      terrainEngine.removeOnMouseMoveListener(handleMouseMove);
      terrainEngine.getContent().getRouteContent().removeRoutes([id]);
    };
  }, [terrainEngine, id, polyline, waypoints, isSelected, t, logError]);

  return popupProps ? <Popup {...popupProps} /> : null;
};
